import ParentStyled from '@/modules/LogsManagement/style';
import { useTheme } from '@material-ui/core/styles';
import { pageUrls } from 'globalconfig/constants/keys';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import { UI_DATE_LOWER_FORMAT, UI_LOGBOOK_DATE } from 'globalconfig/constants/static';
import { getNewObject, hasRestrictedRole, loadPageConfig, loadStyle } from 'globalutils/fmUtils';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Icon = lazy(() => import(`globalcomponents/UI/Icon/${layout}`));
const Tab = lazy(() => import(`globalcomponents/UI/Tab/${layout}`));
const SvgIcon = lazy(() => import(`globalcomponents/SvgIcon/${layout}`));

const config = loadPageConfig();
const DynamicComponent = lazy(() => import(`@/${config.archiveLogsList[0].name}`));
const Styled = loadStyle(ParentStyled, 'modules/LogsManagement');

const Index = (parentProps) => {
  const { manageLogList, fetchLogList, downloadLogPdf, tableTotalCount, getFilters, fetchDrivers, drivers } = parentProps;
  const [pagination, setPagination] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();

  const commonParentProps = { history, i18n: { t }, Styled, theme };

  useEffect(() => {
    getFilters({ apiId: 'fetchFilters' });
    fetchDrivers({ apiId: 'fetchDrivers' });
  }, [getFilters, fetchDrivers]);

  useEffect(() => {
    const element = document.getElementById('list1');
    if (element && !element.className.includes('Mui-selected')) {
      element.className += ' Mui-selected';
    }
  }, []);

  const setSelectedIndexfn = (index) => {
    const rowLength =
      window.$environment.SERVICE_PROVIDER === 'spireon' ? 36 : window.$environment.APP_LAYOUT === 'NewUi' ? 40 : 39;
    const widthLength =
      window.$environment.SERVICE_PROVIDER === 'spireon' ? 104 : window.$environment.APP_LAYOUT === 'NewUi' ? 82 : 74;

    setSelectedIndex(widthLength + rowLength * index);
  };

  return (
    <RenderComponent
      {...config.archiveLogsList[0]}
      parentProps={parentProps}
      drivers={drivers}
      commonParentProps={commonParentProps}
      component={DynamicComponent}
      history={history}
      i18n={{ t }}
      fetchLogList={fetchLogList}
      downloadLogPdf={downloadLogPdf}
      tableTotalCount={tableTotalCount}
      list={manageLogList}
      selectedIndex={selectedIndex}
      setSelectedIndexfn={setSelectedIndexfn}
      pagination={pagination}
      setPagination={setPagination}
    />
  );
};

const RenderComponent = ({
  props,
  commonProps,
  parentProps,
  commonParentProps,
  component: Component,
  history,
  drivers,
  i18n,
  fetchLogList,
  downloadLogPdf,
  tableTotalCount,
  selectedIndex,
  setSelectedIndexfn,
  pagination,
  setPagination,
  ...rest
}) => {
  const [request, setRequest] = useState({ displayLength: '15', displayStart: '1' });
  const [isToolsClicked, setIsToolsClicked] = useState(false);

  const sortColumnMapping = {
    vehicle_name: 'vehicleName',
    driver_name: 'driverName',
    date: 'day',
  };

  useEffect(() => {
    fetchLogList({
      apiId: 'fetchArchiveLoglistApiId',
      data: { ...request },
    });
  }, [request, fetchLogList]);

  const actions = [
    {
      icon: (row) => (
        <Icon
          src={mediaUrls?.download}
          tooltip={i18n.t('Download')}
          onClick={(e) => {
            e.stopPropagation();
            downloadLogPdf({ apiId: 'downloadLogPdf', data: row.dailylog });
          }}
        />
      ),
    },
  ];

  let newProps = props?.length ? getNewObject(parentProps, props) : {};
  newProps = {
    ...newProps,
    ...(commonProps?.length ? getNewObject(commonParentProps, commonProps) : {}),
    ...rest,
  };

  const filterLogsList = (data) => {
    setRequest((prev) => ({ ...prev, ...data, displayLength: '15', displayStart: '1' }));
  };

  const changeRequest = (fields) => {
    const updatedFields = { ...fields };
    if (updatedFields?.sortCol && sortColumnMapping[updatedFields.sortCol]) {
      updatedFields.sortCol = sortColumnMapping[updatedFields.sortCol];
    }
    setRequest((prev) => ({ ...prev, ...updatedFields, displayLength: '15', displayStart: '1' }));
  };

  const selectedRow = (row) => {
    const day = moment(new Date(row.date)).format(UI_LOGBOOK_DATE);
    if (!hasRestrictedRole(['ADMIN'])) {
      history.push(`${pageUrls.logBook.replace(':id', row.idPub)}?driverId=${row.driverId}&logDay=${day}`);
    }
  };

  const tabValues = [
    { name: i18n.t('logs.manageLogsHeader'), url: pageUrls.logsList },
    { name: i18n.t('logs.undefinedDriverHeader'), url: pageUrls.logsUdList },
    ...(window.$environment.SERVICE_PROVIDER !== 'spireon'
      ? [
          { name: i18n.t('logs.addLogHeader'), url: pageUrls.logsAdd },
          {
            name: i18n.t('logs.archiveLogsHeader'),
            url: pageUrls.archiveLogsList,
            value: (
              <Component
                {...newProps}
                selectedRow={(row) => selectedRow(row)}
                isDriverLogs={false}
                actions={actions}
                isServer
                restrictRowClick
                filterLogsList={filterLogsList}
                changeRequest={changeRequest}
                request={request}
                isToolsClicked={isToolsClicked}
                setIsToolsClicked={setIsToolsClicked}
                selectedIndex={selectedIndex}
                pagination={pagination}
                setPagination={setPagination}
                tableTotalCount={tableTotalCount}
                fields={[
                  {
                    type: 'select',
                    suggestions: drivers.data,
                    value: '',
                    field: 'driver_name',
                    placeholder: i18n.t('common.driver'),
                    label: i18n.t('common.driver'),
                  },
                  {
                    type: 'date',
                    value: '',
                    field: 'dateFrom',
                    placeholder: UI_DATE_LOWER_FORMAT,
                    label: i18n.t('common.dateFrom'),
                    isTimeNeeded: false,
                    isClearable: true,
                  },
                  {
                    type: 'date',
                    value: '',
                    field: 'dateTo',
                    placeholder: UI_DATE_LOWER_FORMAT,
                    label: i18n.t('common.dateTo'),
                    isTimeNeeded: false,
                    minValue: 'dateFrom',
                  },
                ]}
              />
            ),
          },
        ]
      : []),
    ...(window.$environment.SERVICE_PROVIDER === 'spireon'
      ? [{ name: i18n.t('leftMenu.DOTAudit'), url: pageUrls.fmcsaLogs }]
      : []),
  ];

  const handleChange = (_, newValue) => {
    history.push(tabValues[newValue].url);
  };

  return (
    <Styled.Content>
      <div className="goBack">
        <SvgIcon value={mediaUrls.pageBackButton} onClick={() => history.goBack()} />
      </div>
      <Tab data={tabValues} className="logwrapper" currentTab={3} onChange={handleChange} vertical={rest.verticalTab} />
    </Styled.Content>
  );
};

const mapState = (state) => ({
  apiStatus: state.asyncRequests,
  manageLogList: state.manageLogList,
  tableTotalCount: state.manageLogList.totalCount,
  filters: state.filters,
  drivers: state.drivers,
});

const mapDispatch = (dispatch) => ({
  fetchLogList: (data) => dispatch.manageLogList.fetchArchiveData(data),
  downloadLogPdf: (data) => dispatch.manageLogList.fetchPDF(data),
  filterSuccess: (data) => dispatch.manageLogList.filterSuccess(data),
  fetchDrivers: (data) => dispatch.drivers.getDrivers(data),
  setSelectedManageLogItem: (data) => dispatch.manageLogList.setSelectedItem(data),
  handleLogBookStatus: (data) => dispatch.logBookStatus.handleLogBookStatus(data),
  clearApiStatus: () => dispatch.asyncRequests.clearApiStatus(),
  getFilters: (data) => dispatch.filters.fetchData(data),
  viewReport: (data) => dispatch.viewReport.viewReportDocument(data),
});

Index.defaultProps = {};
Index.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  manageLogList: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  filterSuccess: PropTypes.func.isRequired,
  fetchDrivers: PropTypes.func.isRequired,
  handleLogBookStatus: PropTypes.func.isRequired,
  clearApiStatus: PropTypes.func.isRequired,
  setSelectedManageLogItem: PropTypes.func.isRequired,
  fetchUdLogList: PropTypes.func.isRequired,
  udFilterSuccess: PropTypes.func.isRequired,
  fetchUnAssignedEntries: PropTypes.func.isRequired,
  unAssignedEntries: PropTypes.object.isRequired,
  saveUnAssignedEntries: PropTypes.func.isRequired,
  setUdAssignEntryList: PropTypes.func.isRequired,
  clearUdAssignEntryListById: PropTypes.func.isRequired,
};

export default connect(mapState, mapDispatch)(Index);
